/**
 * Structural preparation from the Entries a Character holds, before any Rule Element runs
 * (Foundry: the `prepareActorData` of ancestry, heritage, background, class, feat and lore
 * items, then `prepareBuildData` and `setNumericRollOptions`).
 */
import type { EngineItem } from "./items";
import type { EngineState } from "./state";
import { sluggify } from "./slug";
import { ARMOR_CATEGORIES, ATTRIBUTES, SAVES, SIZES, SIZE_SLUGS, SKILL_SLUGS, WEAPON_CATEGORIES, clampRank, isAttribute, isSkillSlug, type Attribute, type Rank } from "./values";

type BoostSlot = { value?: unknown; selected?: unknown };

/** The attribute a boost slot resolves to: the answer when the slot offers it (Foundry clears one the slot no longer offers), else the single option a fixed slot offers. */
function selectedBoost(slot: BoostSlot, answer: unknown): Attribute | null {
  const options = Array.isArray(slot.value) ? slot.value.filter(isAttribute) : [];
  if (isAttribute(answer) && options.includes(answer)) return answer;
  if (isAttribute(slot.selected)) return slot.selected;
  return options.length === 1 ? options[0]! : null;
}

function slotEntries(record: unknown): [string, BoostSlot][] {
  if (typeof record !== "object" || record === null) return [];
  return Object.entries(record as Record<string, unknown>)
    .filter((e): e is [string, BoostSlot] => typeof e[1] === "object" && e[1] !== null)
    .sort(([a], [b]) => Number(a) - Number(b));
}

export function prepareAncestry(state: EngineState, item: EngineItem): void {
  const { actor } = state;
  const system = item.system;
  const hp = typeof system.hp === "number" ? system.hp : 0;
  actor.system.attributes.ancestryhp = hp;
  logAutoChange(state, "system.attributes.ancestryhp", hp, item);
  const size = typeof system.size === "string" && (SIZES as readonly string[]).includes(system.size) ? system.size : "med";
  actor.system.traits.size.value = size;
  logAutoChange(state, "system.traits.size.value", size, item);
  const reach = typeof system.reach === "number" ? system.reach : 5;
  actor.system.attributes.reach = { base: reach, manipulate: reach };
  const speed = typeof system.speed === "number" ? system.speed : 25;
  actor.system.movement.speeds.land = { value: speed, base: speed, source: item.name };
  actor.system.attributes.speed.value = speed;
  const hands = typeof system.hands === "number" ? system.hands : 2;
  actor.system.hands.max = { value: hands, active: hands };

  const build = actor.system.build;
  const alternate = system.alternateAncestryBoosts;
  if (item.answers.alternateBoosts === true || Array.isArray(alternate)) {
    const boosts = Array.isArray(alternate) ? alternate.filter(isAttribute) : [];
    if (boosts.length > 0) build.attributes.boosts.ancestry.push(...boosts);
    else for (const key of ["alternate:0", "alternate:1"]) if (isAttribute(item.answers[key])) build.attributes.boosts.ancestry.push(item.answers[key]);
  } else {
    for (const [index, slot] of slotEntries(system.boosts)) {
      const boost = selectedBoost(slot, item.answers[`boost:${index}`]);
      if (boost) build.attributes.boosts.ancestry.push(boost);
    }
    for (const [index, slot] of slotEntries(system.flaws)) {
      const flaw = selectedBoost(slot, item.answers[`flaw:${index}`]);
      if (flaw) build.attributes.flaws.ancestry.push(flaw);
    }
  }
  // Voluntary flaws (Player Core): one extra boost paid for with two flaws.
  if (isAttribute(item.answers["voluntary:boost"])) build.attributes.boosts.ancestry.push(item.answers["voluntary:boost"]);
  for (const key of ["voluntary:flaw:0", "voluntary:flaw:1"]) if (isAttribute(item.answers[key])) build.attributes.flaws.ancestry.push(item.answers[key]);

  const additional = (system.additionalLanguages as { count?: unknown } | undefined)?.count;
  build.languages.max += typeof additional === "number" ? additional : 0;
  const languages = (system.languages as { value?: unknown } | undefined)?.value;
  if (Array.isArray(languages)) {
    for (const language of languages) {
      if (typeof language === "string" && !build.languages.granted.some((l) => l.slug === language)) build.languages.granted.push({ slug: language, source: item.name });
    }
  }

  const vision = system.vision;
  if (typeof vision === "string" && vision !== "normal" && !actor.system.perception.senses.some((s) => s.type === vision)) {
    actor.system.perception.senses.push({ type: vision, acuity: "precise", range: Infinity, source: item.name });
    state.rollOptions.set("sense", `self:${sluggify(vision)}:from-ancestry`);
  }

  const traits = item.traits;
  actor.system.traits.value.push(...traits);
  actor.system.details.ancestry = { name: item.name, trait: item.slug, countsAs: [item.slug] };
  state.rollOptions.set("all", `self:ancestry:${item.slug}`);
  for (const trait of traits) state.rollOptions.set("all", `self:trait:${trait}`);
}

export function prepareHeritage(state: EngineState, item: EngineItem): void {
  const { actor } = state;
  actor.system.details.heritage = { name: item.name, trait: item.slug };
  actor.system.traits.value.push(...item.traits);
  // A versatile heritage (one with no ancestry) counts as an ancestry for feat eligibility.
  if (item.system.ancestry === null) actor.system.details.ancestry?.countsAs.push(item.slug);
  state.rollOptions.set("all", `heritage:${item.slug}`);
  state.rollOptions.set("all", `self:heritage:${item.slug}`);
}

export function prepareBackground(state: EngineState, item: EngineItem): void {
  const { actor } = state;
  for (const [index, slot] of slotEntries(item.system.boosts)) {
    const boost = selectedBoost(slot, item.answers[`boost:${index}`]);
    if (boost) actor.system.build.attributes.boosts.background.push(boost);
  }
  const trained = item.system.trainedSkills as { value?: unknown; lore?: unknown } | undefined;
  if (Array.isArray(trained?.value)) {
    for (const key of trained.value) {
      const skill = typeof key === "string" ? actor.system.skills[key] : undefined;
      if (skill) skill.rank = Math.max(skill.rank, 1) as Rank;
    }
  }
  if (Array.isArray(trained?.lore)) {
    for (const name of trained.lore) if (typeof name === "string" && name) ensureLore(state, name, 1);
  }
}

/**
 * Foundry's `DeityPF2e#prepareActorData`: the held deity's skills, favoured weapons, fonts and
 * domains (with the domain labels of the deityDomains table) on the details, and the roll options
 * the Divine Font and domain ChoiceSets read. Favoured-weapon proficiency is the inventory's (#11).
 */
export function prepareDeity(state: EngineState, item: EngineItem): void {
  const { actor } = state;
  const system = item.system;
  const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []);
  const domains = system.domains as { primary?: unknown; alternate?: unknown } | undefined;
  const labels = state.catalogue?.table("deityDomains") ?? {};
  const spells: Record<string, string> = {};
  if (typeof system.spells === "object" && system.spells !== null) {
    for (const [rank, uuid] of Object.entries(system.spells as Record<string, unknown>)) if (typeof uuid === "string" && uuid) spells[rank] = uuid;
  }
  const font = strings(system.font);
  const deities = actor.system.details.deities;
  deities.primary = { slug: item.slug, name: item.name, skill: strings(system.skill), weapons: strings(system.weapons), font, spells };
  for (const domain of strings(domains?.primary)) {
    deities.domains[domain] = labels[domain] ?? domain;
    if (`${domain}-apocryphal` in labels) deities.domains[`${domain}-apocryphal`] = labels[`${domain}-apocryphal`]!;
  }
  const options = state.rollOptions;
  options.set("all", "deity");
  options.set("all", `deity:primary:${item.slug}`);
  const sanctification = system.sanctification as { modal?: unknown; what?: unknown } | null | undefined;
  if (sanctification && typeof sanctification === "object") {
    for (const what of strings(sanctification.what)) options.set("all", `deity:primary:sanctification:${sanctification.modal === "must" ? "must" : "can"}:${what}`);
  }
  for (const weapon of strings(system.weapons)) options.set("all", `deity:primary:favored-weapon:${weapon}`);
  for (const f of font) options.set("all", `deity:primary:font:${f}`);
  options.set("all", `self:deity:slug:${item.slug}`);
}

/** A Lore skill (Foundry keeps these as `lore` items). */
export function ensureLore(state: EngineState, name: string, rank: Rank): void {
  const slug = sluggify(name);
  const skills = state.actor.system.skills;
  const existing = skills[slug];
  if (existing) existing.rank = Math.max(existing.rank, rank) as Rank;
  else skills[slug] = { rank, attribute: "int", armor: false, lore: true, label: name, value: 0 };
}

/**
 * Foundry's `ClassPF2e#prepareActorData`. Under Dual Class (#12) the second class runs through
 * here too with `primary` false: it merges (the higher HP, the better of each proficiency, the
 * union of key attribute options and trained skills, its own class DC) and leaves the key
 * attribute answer and the primary class DC to the first.
 */
export function prepareClass(state: EngineState, item: EngineItem, primary = true): void {
  const { actor } = state;
  const system = item.system;
  const { attributes, build, details, proficiencies, saves, skills } = actor.system;
  const keyAbility = system.keyAbility as { value?: unknown; selected?: unknown } | undefined;
  const keyOptions = Array.isArray(keyAbility?.value) ? keyAbility.value.filter(isAttribute) : [];
  build.attributes.keyOptions = [...new Set([...build.attributes.keyOptions, ...keyOptions])];
  if (primary) build.attributes.boosts.class = selectedBoost({ value: keyOptions, selected: keyAbility?.selected }, item.answers.keyAbility);

  const hp = typeof system.hp === "number" ? system.hp : 0;
  attributes.classhp = Math.max(attributes.classhp, hp);
  const perception = clampRank(system.perception);
  actor.system.perception.rank = Math.max(actor.system.perception.rank, perception) as Rank;
  logAutoChange(state, "system.perception.rank", perception, item);

  if (primary) details.keyability.value = build.attributes.boosts.class ?? "str";
  const classAttribute = primary ? details.keyability.value : (selectedBoost({ value: keyOptions, selected: keyAbility?.selected }, item.answers.keyAbility) ?? keyOptions[0] ?? details.keyability.value);
  proficiencies.classDCs[item.slug] = { label: item.name, rank: 1, attribute: classAttribute, primary };
  logAutoChange(state, `system.proficiencies.classDCs.${item.slug}.rank`, 1, item);

  const attacks = (system.attacks ?? {}) as Record<string, unknown>;
  for (const category of WEAPON_CATEGORIES) {
    const rank = clampRank(attacks[category]);
    const current = (proficiencies.attacks[category] ??= { rank: 0, visible: true });
    current.rank = Math.max(current.rank, rank) as Rank;
    logAutoChange(state, `system.proficiencies.attacks.${category}.rank`, rank, item);
  }
  const defenses = (system.defenses ?? {}) as Record<string, unknown>;
  for (const category of ARMOR_CATEGORIES) {
    if (!(category in defenses)) continue;
    const rank = clampRank(defenses[category]);
    const current = (proficiencies.defenses[category] ??= { rank: 0, visible: true });
    current.rank = Math.max(current.rank, rank) as Rank;
    logAutoChange(state, `system.proficiencies.defenses.${category}.rank`, rank, item);
  }
  const savingThrows = (system.savingThrows ?? {}) as Record<string, unknown>;
  for (const save of SAVES) {
    const rank = clampRank(savingThrows[save]);
    saves[save].rank = Math.max(saves[save].rank, rank) as Rank;
    logAutoChange(state, `system.saves.${save}.rank`, rank, item);
  }
  const trained = system.trainedSkills as { value?: unknown; additional?: unknown } | undefined;
  if (Array.isArray(trained?.value)) {
    for (const key of trained.value) {
      const skill = typeof key === "string" ? skills[key] : undefined;
      if (skill) skill.rank = Math.max(skill.rank, 1) as Rank;
    }
  }
  // The class's free trained skills are answers on the class Choice: `skill:0`, `skill:1`, ...
  for (const [key, value] of Object.entries(item.answers)) {
    if (!/^skill:\d+$/.test(key)) continue;
    if (isSkillSlug(value)) skills[value]!.rank = Math.max(skills[value]!.rank, 1) as Rank;
    else if (typeof value === "string" && value) ensureLore(state, value, 1);
  }
  const spellcasting = clampRank(system.spellcasting);
  proficiencies.spellcasting.rank = Math.max(proficiencies.spellcasting.rank, spellcasting) as Rank;
  logAutoChange(state, "system.proficiencies.spellcasting.rank", spellcasting, item);

  if (primary) details.class = { name: item.name, trait: item.slug };
  state.rollOptions.set("all", `class:${item.slug}`);
}

export function prepareFeat(state: EngineState, item: EngineItem): void {
  const { actor } = state;
  // Swap the standardized "ancestry" trait for the Character's ancestry trait.
  const traits = (item.system.traits as { value?: string[] } | undefined)?.value;
  if (Array.isArray(traits)) {
    const index = traits.indexOf("ancestry");
    if (index !== -1) {
      const ancestrySlug = actor.system.details.ancestry?.trait;
      if (ancestrySlug) traits.splice(index, 1, ancestrySlug);
      else traits.splice(index, 1);
    }
  }
  const prefix = item.isFeature ? "feature" : "feat";
  state.rollOptions.set("all", `${prefix}:${item.slug}`);

  const subfeatures = item.system.subfeatures as { keyOptions?: unknown; languages?: { slots?: unknown; granted?: unknown }; proficiencies?: Record<string, { rank?: unknown; attribute?: unknown }> } | undefined;
  if (!subfeatures) return;
  const { build, proficiencies, saves } = actor.system;
  if (Array.isArray(subfeatures.keyOptions) && subfeatures.keyOptions.length > 0) {
    build.attributes.keyOptions = [...new Set([...build.attributes.keyOptions, ...subfeatures.keyOptions.filter(isAttribute)])];
  }
  if (typeof subfeatures.languages?.slots === "number") build.languages.max += subfeatures.languages.slots;
  if (Array.isArray(subfeatures.languages?.granted)) {
    for (const slug of subfeatures.languages.granted) if (typeof slug === "string") build.languages.granted.push({ slug, source: item.name });
  }
  for (const [slug, increase] of Object.entries(subfeatures.proficiencies ?? {})) {
    const rank = clampRank(increase?.rank);
    if (!rank) continue;
    const proficiency = ((): { rank: Rank } | null => {
      if (slug === "perception") return actor.system.perception;
      if (slug === "spellcasting") return proficiencies.spellcasting;
      if ((SAVES as readonly string[]).includes(slug)) return saves[slug as (typeof SAVES)[number]];
      if ((WEAPON_CATEGORIES as readonly string[]).includes(slug)) return proficiencies.attacks[slug]!;
      if ((ARMOR_CATEGORIES as readonly string[]).includes(slug)) return proficiencies.defenses[slug]!;
      if (/^[a-z][a-z-]*$/.test(slug)) {
        const attribute = isAttribute(increase?.attribute) ? increase.attribute : "str";
        return (proficiencies.classDCs[slug] ??= { attribute, label: slug, rank: 0, primary: false });
      }
      return null;
    })();
    if (proficiency) proficiency.rank = Math.max(proficiency.rank, rank) as Rank;
  }
}

/** Foundry's `prepareBuildData`: turn boosts and flaws into attribute modifiers. */
export function prepareBuildData(state: EngineState): void {
  const { abilities, build } = state.actor.system;
  if (!build.attributes.manual) {
    const sections = ["ancestry", "background", "class", 1, 5, 10, 15, 20] as const;
    for (const section of sections) {
      const boosts = build.attributes.boosts[section];
      if (typeof boosts === "string") {
        const ability = abilities[boosts];
        ability.mod += ability.mod >= 4 ? 0.5 : 1;
      } else if (Array.isArray(boosts)) {
        for (const abbrev of boosts) {
          const ability = abilities[abbrev];
          ability.mod += ability.mod >= 4 ? 0.5 : 1;
        }
      }
      const flaws = section === "ancestry" ? build.attributes.flaws.ancestry : [];
      for (const abbrev of flaws) abilities[abbrev].mod -= 1;
    }
    const isABP = state.variantRules.automaticBonusProgression;
    if (build.attributes.apex && (!isABP || state.level >= 17)) {
      const attribute = abilities[build.attributes.apex];
      attribute.mod = Math.max(attribute.mod + 1, 4);
    }
  }
  for (const attribute of Object.values(abilities)) {
    attribute.mod = Math.min(Math.max(attribute.mod, -5), 10);
    attribute.base = Math.trunc(attribute.mod);
  }
}

/** Foundry's `setNumericRollOptions`, run after ActiveEffectLike changes. */
export function setNumericRollOptions(state: EngineState): void {
  const { actor, rollOptions } = state;
  const system = actor.system;
  rollOptions.set("all", `perception:rank:${system.perception.rank}`);
  for (const key of ATTRIBUTES) rollOptions.set("all", `attribute:${key}:mod:${system.abilities[key].mod}`);
  for (const key of SKILL_SLUGS) rollOptions.set("all", `skill:${key}:rank:${system.skills[key]?.rank ?? 0}`);
  for (const key of WEAPON_CATEGORIES) rollOptions.set("all", `attack:${key}:rank:${system.proficiencies.attacks[key]?.rank ?? 0}`);
  for (const key of ARMOR_CATEGORIES) rollOptions.set("all", `defense:${key}:rank:${system.proficiencies.defenses[key]?.rank ?? 0}`);
  for (const key of SAVES) rollOptions.set("all", `save:${key}:rank:${system.saves[key].rank}`);
  system.attributes.handsFree = system.hands.free.value;
  rollOptions.set("all", `hands-free:${system.hands.free.value}`);
  rollOptions.set("all", `hands-free:but-really:${system.hands.free.really}`);
}

/** Roll options set once statistics are about to be derived (creature and base actor prepareDerivedData). */
export function setDerivedRollOptions(state: EngineState): void {
  const { actor, rollOptions } = state;
  const traits = actor.system.traits.value;
  for (const trait of traits) rollOptions.set("all", `self:trait:${trait}`);
  const mode = traits.includes("undead") ? "undead" : traits.includes("construct") ? "construct" : "living";
  rollOptions.set("all", `self:mode:${mode}`);
  if (actor.system.attributes.hp.negativeHealing) rollOptions.set("all", "self:negative-healing");
  const sizeIndex = Math.max(0, (SIZES as readonly string[]).indexOf(actor.system.traits.size.value));
  rollOptions.set("all", `self:size:${sizeIndex}`);
  rollOptions.set("all", `self:size:${SIZE_SLUGS[sizeIndex]}`);
  if (actor.system.attributes.familiarAbilities.value > 0) rollOptions.set("all", "self:has-familiar");
}

function logAutoChange(state: EngineState, path: string, value: number | string, item: EngineItem): void {
  (state.actor.system.autoChanges[path] ??= []).push({ mode: "override", level: item.level, value, source: item.name });
}
